import type {
  ItemTagState,
  TagAssignment,
} from "~/ui-rtk/types/structured-tags"
import { emptySplitApi } from "./emptyApi"

interface GetAssignmentsParams {
  itemType: string
  itemId: string
}

interface BulkSetTagsParams {
  itemType: string
  itemId: string
  tags: ItemTagState
  /** Raw typed values for free_text categories, resolved server-side on save. */
  freeTextTags?: Record<string, string[]>
}

interface BulkGetTagsParams {
  itemType: string
  itemIds: string[]
}

/** Map of itemId -> { categorySlug -> assignments[] } */
export type BulkTagAssignmentsResponse = Record<
  string,
  Record<string, TagAssignment[]>
>

interface AutoSuggestParams {
  adName: string
  adSetName?: string
  campaignName?: string
  platform?: string
  existingTags?: Record<string, string[]>
}

export interface AutoTagSuggestionValue {
  value: string
  label: string
  confidence: number
  isNew: boolean
}

export interface AutoTagSuggestionEntry {
  values: AutoTagSuggestionValue[]
  /** AI-proposed metadata when the category does not exist yet */
  categoryLabel?: string
  isNewCategory?: boolean
  allowMultiple?: boolean
  valueType?: "predefined" | "user_generated" | "mixed"
}

export interface AutoTagSuggestionResponse {
  suggestions: Record<string, AutoTagSuggestionEntry>
  reasoning: string
}

interface SingleAssignParams {
  categorySlug: string
  optionId: string
  itemType: string
  itemId: string
}

export const tagAssignmentsApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getTagAssignments: builder.query<
      Record<string, TagAssignment[]>,
      GetAssignmentsParams
    >({
      query: ({ itemType, itemId }) => `tag-assignments/${itemType}/${itemId}`,
      providesTags: (_result, _error, { itemType, itemId }) => [
        { type: "TagAssignment", id: `${itemType}:${itemId}` },
      ],
    }),

    bulkGetTagAssignments: builder.query<
      BulkTagAssignmentsResponse,
      BulkGetTagsParams
    >({
      query: ({ itemType, itemIds }) => ({
        url: "tag-assignments/bulk-get",
        method: "POST",
        body: { itemType, itemIds },
      }),
      providesTags: (result, _error, { itemType }) =>
        result
          ? [
              ...Object.keys(result).map(itemId => ({
                type: "TagAssignment" as const,
                id: `${itemType}:${itemId}`,
              })),
              { type: "TagAssignment", id: "BULK" },
            ]
          : [{ type: "TagAssignment", id: "BULK" }],
    }),

    bulkSetTags: builder.mutation<
      Record<string, TagAssignment[]>,
      BulkSetTagsParams
    >({
      query: ({ itemType, itemId, tags, freeTextTags }) => ({
        url: `tag-assignments/${itemType}/${itemId}`,
        method: "PUT",
        body: { tags, freeTextTags },
      }),
      invalidatesTags: (_result, _error, { itemType, itemId }) => [
        { type: "TagAssignment", id: `${itemType}:${itemId}` },
        // Also force a bulk refetch as a fallback in case the optimistic
        // patch misses an active subscription. Without this, the table
        // could lag until the next reload.
        { type: "TagAssignment", id: "BULK" },
        // Also refetch categories: the first save against a virtual system
        // category materializes it server-side, swapping the virtual id for
        // a real DB id. The UI needs to pick up the real ids on next render.
        { type: "TagCategory", id: "LIST" },
      ],
      // Patch every active `bulkGetTagAssignments` cache entry that includes
      // this itemId so the table updates without waiting for a refetch (which
      // happens too — but RTK Query's invalidation->refetch round-trip is what
      // was leaving the table looking stale until a manual refresh).
      async onQueryStarted(
        { itemType, itemId },
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data } = await queryFulfilled
          const cachedArgs = tagAssignmentsApi.util.selectCachedArgsForQuery(
            getState() as unknown as Parameters<
              typeof tagAssignmentsApi.util.selectCachedArgsForQuery
            >[0],
            "bulkGetTagAssignments",
          )
          for (const args of cachedArgs) {
            if (args.itemType !== itemType) continue
            if (!args.itemIds.includes(itemId)) continue
            dispatch(
              tagAssignmentsApi.util.updateQueryData(
                "bulkGetTagAssignments",
                args,
                draft => {
                  draft[itemId] = data
                },
              ),
            )
          }
        } catch {
          // Mutation failed — leave caches alone, the invalidation refetch
          // will reconcile if it succeeds later.
        }
      },
    }),

    assignTag: builder.mutation<TagAssignment, SingleAssignParams>({
      query: body => ({
        url: "tag-assignments",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { itemType, itemId }) => [
        { type: "TagAssignment", id: `${itemType}:${itemId}` },
        { type: "TagAssignment", id: "BULK" },
      ],
    }),

    autoSuggestTags: builder.mutation<
      AutoTagSuggestionResponse,
      AutoSuggestParams
    >({
      query: body => ({
        url: "tag-assignments/auto-suggest",
        method: "POST",
        body,
      }),
    }),

    removeTagAssignment: builder.mutation<
      void,
      { id: string; itemType: string; itemId: string }
    >({
      query: ({ id }) => ({
        url: `tag-assignments/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { itemType, itemId }) => [
        { type: "TagAssignment", id: `${itemType}:${itemId}` },
        { type: "TagAssignment", id: "BULK" },
      ],
    }),
  }),
})

export const {
  useGetTagAssignmentsQuery,
  useBulkGetTagAssignmentsQuery,
  useBulkSetTagsMutation,
  useAssignTagMutation,
  useAutoSuggestTagsMutation,
  useRemoveTagAssignmentMutation,
} = tagAssignmentsApi
